import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, List, ListItem, ListItemIcon, ListItemText, Badge, Drawer,
    Collapse, Divider, Paper, Grid, Container, Button, Fab, IconButton, Tooltip,
    useTheme, useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/system';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PaymentIcon from '@mui/icons-material/Payment';
import GppGoodIcon from '@mui/icons-material/GppGood';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import BuildIcon from '@mui/icons-material/Build';
import MapIcon from '@mui/icons-material/Map';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TuneIcon from '@mui/icons-material/Tune';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SupportIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FeedbackIcon from '@mui/icons-material/Feedback';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ChatIcon from '@mui/icons-material/Chat';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PolicyIcon from '@mui/icons-material/Policy';
import DescriptionIcon from '@mui/icons-material/Description';
import LogoutIcon from '@mui/icons-material/Logout';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import SchoolIcon from '@mui/icons-material/School';
import UserManagement from './UserManagement';
import UserProfile from './UserProfile';
import EquipmentManagement from './EquipmentManagement';
import TemplateManagement from './TemplateManagement';
import LAndTMonitoring from './LAndTMonitoring';
import VAndSControl from './VAndSControl';
import ReviewManagement from './ReviewManagement';
import Directory from './Directory';
import SD_logo from '../assests/shipperdock.png';
import AdminBulkLoadPosting from './AdminBulkLoadPosting';
import AdminBulkTruckPosting from './AdminBulkTruckPosting';
import TrialBilling from './TrialBilling';
import PaymentMethod from './PaymentMethod';
import FeedbackManagement from './FeedbackManagement';
import UpdatesAndTipsManagement from './UpdatesAndTipsManagement';
import TrainingManagement from './TrainingManagement';
import UpdatesAndTipsViewer from './UpdatesAndTipsViewer';
import Notifications from './Notifications';
import axios from 'axios';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';
import TrainingViewer from './TrainingViewer'; 
import TemplatesViewer from './TemplatesViewer';
import SupportForum from './SupportForum';
import MarketMap from './MarketMap';
import HelpCenter from './HelpCenter';
import CompanyProfile from './CompanyProfile';
import AdminChat from './AdminChat';
import ActiveSessions from './ActiveSessions';
import CarrierApproval from './CarrierApproval';
import PostCarriers from './PostCarriers';
import PostBrokers from './PostBrokers';
import PostShippers from './PostShippers';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';


const theme = createTheme({
    palette: {
        primary: { main: '#11224d' },
        secondary: { main: '#0e1d3a' },
        warning: { main: '#ffb300' },
    },
    typography: { fontFamily: 'Inter, Arial, sans-serif' },
});

const drawerWidth = 240;
const collapsedWidth = 70;
const dashboardHeaderHeight = '44px';

const StyledBox = styled(Box)({
    display: 'flex',
    minHeight: '100vh',
});

const Sidebar = styled(Box)(({ theme, collapsed }) => ({
    width: collapsed ? collapsedWidth : drawerWidth,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    background: 'linear-gradient(to bottom, #0e1d3a, #11224d)',
    color: 'white',
    boxShadow: '2px 0 5px rgba(0, 0, 0, 0.2)',
    position: 'fixed',
    height: '100vh',
    zIndex: 1200,
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
    }),
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const SidebarItem = styled(ListItem)(({ theme, collapsed }) => ({
    cursor: 'pointer',
    justifyContent: collapsed ? 'center' : 'flex-start',
    paddingLeft: collapsed ? theme.spacing(1) : theme.spacing(2),
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.Mui-selected': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderLeft: collapsed ? 'none' : '4px solid #4D9DE0',
        backgroundColor: collapsed ? 'rgba(77, 157, 224, 0.3)' : 'rgba(255, 255, 255, 0.2)',
    },
}));

const NestedSidebarItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingLeft: theme.spacing(2),
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
}));

const InfoCard = styled(Paper)(({ theme }) => ({
    width: '290px', 
    margin: '0 auto',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    backgroundColor: 'white',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const StyledDashboardButton = styled(Button)(({ theme }) => ({
    padding: '5px 10px',
    borderRadius: '7px',
    fontWeight: 'bold',
    color: 'white',
    borderColor: theme.palette.grey[400],
    textTransform: 'uppercase',
    fontSize: '0.65rem',
    '&:hover': {
        backgroundColor: theme.palette.primary.main, 
        opacity: 0.9, 
    },
}));

// ── CARRIER-STYLE HEADER ──
const DashboardHeader = styled(Box)(({ theme, collapsed }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0',
    background: 'linear-gradient(135deg, #facc15 0%, #f59e0b 100%)',
    backdropFilter: 'blur(10px)',
    borderBottom: '3px solid #dfa419',
    borderTop: '2px solid rgba(255,255,255,0.3)',
    position: 'fixed',
    top: 0,
    right: 0,
    zIndex: 1099,
    height: '44px',
    boxSizing: 'border-box',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    transition: theme.transitions.create(['width', 'left'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
    }),
}));

const NavTab = styled(Box)(({ active }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 20px',
    height: '100%',
    alignSelf: 'stretch',
    cursor: 'pointer',
    position: 'relative',
    fontSize: '0.68rem',
    fontWeight: 800,
    fontFamily: "'Inter', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
    color: active ? '#0f172a' : 'rgba(0,0,0,0.7)',
    background: active ? '#ffffff' : 'transparent',
    borderRight: '2px solid rgba(0,0,0,0.13)',
    borderBottom: active ? '3px solid #000000' : '3px solid transparent',
    transition: 'all 0.2s ease',

    '&:hover': {
        background: '#0e1d3a',
        color: '#ffffff',
        '& .MuiSvgIcon-root': {
            color: '#facc15',
            transform: 'scale(1.05)',
        },
    },

    '& .MuiSvgIcon-root': {
        fontSize: '0.9rem',
        fontWeight: 900,
        color: active ? '#0f172a' : 'rgba(0,0,0,0.65)',
        transition: '0.2s',
    },

    '&:active': {
        transform: 'scale(0.97)',
    },
}));

const LogoutTab = styled(NavTab)(() => ({
    color: '#ffffff', // Red color
    borderRight: 'none',
    borderLeft: '2px solid rgba(0,0,0,0.13)',
    '& .MuiSvgIcon-root': {
        color: '#ffffff',
    },
    '&:hover': {
        background: '#dc2626', // Turns red on hover
        color: '#ffffff',
        '& .MuiSvgIcon-root': {
            color: '#ffffff', // Icon turns white on hover
        },
    },
}));

const PageNameTag = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 20px',
    height: '100%',
    cursor: 'pointer',
    position: 'relative',
    borderLeft: '2px solid rgba(0,0,0,0.13)',
    background: 'transparent',
    transition: 'background 0.2s ease',
    '&:hover': {
        background: '#0e1d3a',
        '& .page-dot': {
            backgroundColor: '#facc15',
        },
        '& .page-label': {
            color: '#fff',
        },
    },
    '&:active': {
        transform: 'scale(0.97)',
    },
}));

const MainContent = styled(Box)(({ theme, collapsed }) => ({
    flexGrow: 1,
    backgroundColor: '#f4f6f8',
    marginLeft: collapsed ? collapsedWidth : drawerWidth,
    [theme.breakpoints.down('md')]: {
        marginLeft: 0,
    },
    transition: theme.transitions.create('margin-left', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
    }),
    minHeight: '100vh',
    paddingTop: `calc(${dashboardHeaderHeight} + 24px)`,
    paddingX: '24px',
    paddingBottom: '24px',
}));

const ChatFab = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.warning.main,
    '&:hover': {
        backgroundColor: theme.palette.secondary.main,
    },
    zIndex: 1300,
    boxShadow: '0 4px 20px rgba(17, 34, 77, 0.4)',
}));

const AdminDashboard = ({ onLogout }) => {
    const navigate = useNavigate();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

    const toolsRef = useRef(null);
    const supportRef = useRef(null);
    const accountRef = useRef(null);
    const [openSupport, setOpenSupport] = useState(() => {
        return sessionStorage.getItem('admin_openSupport') === 'true';
    });
    const [openAccount, setOpenAccount] = useState(() => {
        return sessionStorage.getItem('admin_openAccount') === 'true';
    });
    const [openTools, setOpenTools] = useState(() => {
        return sessionStorage.getItem('admin_openTools') === 'true';
    });
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return sessionStorage.getItem('admin_sidebarCollapsed') === 'true';
    });
    const [currentPage, setCurrentPage] = useState(() => {
        return sessionStorage.getItem('admin_currentPage') || 'Dashboard';
    });
    const [unreadCount, setUnreadCount] = useState(0);
    const [currentUser, setCurrentUser] = useState(null);
    const [previousPage, setPreviousPage] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        sessionStorage.setItem('admin_openSupport', openSupport);
    }, [openSupport]);

    useEffect(() => {
        sessionStorage.setItem('admin_openAccount', openAccount);
    }, [openAccount]);

    useEffect(() => {
        sessionStorage.setItem('admin_openTools', openTools);
    }, [openTools]);

    useEffect(() => {
        sessionStorage.setItem('admin_currentPage', currentPage);
    }, [currentPage]);

    useEffect(() => {
        sessionStorage.setItem('admin_sidebarCollapsed', isCollapsed);
    }, [isCollapsed]);

    const sendHeartbeat = async () => {
        const token = localStorage.getItem('jwtToken');
        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        if (!token) return;
        try {
            await axios.post(`${API_BASE}/api/auth/heartbeat`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (err) {
            // Silent fail
        }
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    useEffect(() => {
        const role = localStorage.getItem('role');
        if (!role) {
            navigate('/');
        } else if (role !== 'admin') {
            navigate(`/dashboard/${role}`);
        }
    }, [navigate]);

    const handleSmoothScroll = (ref) => {
        setTimeout(() => {
            if (ref.current) {
                ref.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start',
                    inline: 'nearest'
                });
            }
        }, 300);
    };

    const handleToolsClick = () => {
        if (isCollapsed) return;
        const isOpening = !openTools;
        setOpenTools(isOpening);
        if (isOpening) {
            setOpenSupport(false);
            setOpenAccount(false);
            handleSmoothScroll(toolsRef);
        }
    };

    const handleSupportClick = () => {
        if (isCollapsed) return;
        const isOpening = !openSupport;
        setOpenSupport(isOpening);
        if (isOpening) {
            setOpenTools(false);
            setOpenAccount(false);
            handleSmoothScroll(supportRef);
        }
    };

    const handleAccountClick = () => {
        if (isCollapsed) return;
        const isOpening = !openAccount;
        setOpenAccount(isOpening);
        if (isOpening) {
            setOpenTools(false);
            setOpenSupport(false);
            handleSmoothScroll(accountRef);
        }
    };

    const fetchUnreadCount = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
            const res = await axios.get(`${API_BASE_URL}/api/notifications/unread-count`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUnreadCount(res.data.count);
        } catch (err) {
            console.error("Failed to fetch admin unread notification count.", err);
        }
    };

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            if (!user.role) {
                user.role = localStorage.getItem('role');
            }
            setCurrentUser(user);
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(sendHeartbeat, 120000);
        return () => clearInterval(interval);
    }, []);

    const handleNotificationClick = (page) => {
        if (!page) return;
        handleNavigation(page);
    };

    useEffect(() => {
        fetchUnreadCount();
    }, [currentPage]);

    const handleNavigation = (page, source = null) => {
        if (currentPage === page) {
            setRefreshKey(prev => prev + 1);
        } else {
            setCurrentPage(page);
            setPreviousPage(source);
            setRefreshKey(0);
        }
    };

    useEffect(() => {
        if (currentPage !== 'Help Center') {
            setPreviousPage(null);
        }
    }, [currentPage]);

    const renderPage = () => {
        switch (currentPage) {
            case 'User Management':
                return <UserManagement key={refreshKey} />;
            case 'Trial and Billing Control':
                return <TrialBilling key={refreshKey} />;
            case 'Verification and Status Control':
                return <VAndSControl key={refreshKey} />;
            case 'Equipment Management':
                return <EquipmentManagement key={refreshKey} />;
            case 'Load and Truck Monitoring':
                return <LAndTMonitoring key={refreshKey} />;
            case 'Post Carriers':
                return <PostCarriers key={refreshKey} />;
            case 'Post Brokers':
                return <PostBrokers key={refreshKey} />;
            case 'Bulk Post Loads':
                return <AdminBulkLoadPosting key={refreshKey} />;
            case 'Post Shippers':
                return <PostShippers key={refreshKey} />;
            case 'Bulk Post Trucks':
                return <AdminBulkTruckPosting key={refreshKey} />;
            case 'Review Management':
                return <ReviewManagement key={refreshKey} />;
            case 'Directory':
                return <Directory key={refreshKey} />;
            case 'Account Information':
                return <UserProfile key={refreshKey} onUpdate={fetchUnreadCount} />;
            case 'View Feedback':
                return <FeedbackManagement key={refreshKey} />;
            case 'Updates and Tips':
                return <UpdatesAndTipsManagement key={refreshKey} />;
            case 'Training Management':
                return <TrainingManagement key={refreshKey} />;
            case 'Admin Chat':
                return <AdminChat key={refreshKey} />;
            case 'Active Sessions':
                return <ActiveSessions key={refreshKey} />;
            case 'Company Profile':
                return <CompanyProfile key={refreshKey} />;
            case 'User Approval':
                return <CarrierApproval key={refreshKey} />;
            case 'Template Management':
                return <TemplateManagement key={refreshKey} />;
            case 'Training':
                return <TrainingViewer key={refreshKey} />;
            case 'Templates':
                return <TemplatesViewer key={refreshKey} />;
            case 'Market Map':
                return <MarketMap key={refreshKey} />;
            case 'Community Forum':
                return <SupportForum key={refreshKey} currentUser={currentUser} onNavigate={handleNavigation} />;
            case 'Support Moderation':
                return <SupportForum key={refreshKey} currentUser={currentUser} initialView="moderation" onNavigate={handleNavigation} />;
            case 'View Updates':
                return <UpdatesAndTipsViewer key={refreshKey} />;
            case 'Notifications':
                return <Notifications key={refreshKey} onNotificationsRead={() => setUnreadCount(0)} onNotificationClick={handleNotificationClick} />;
            case 'Payment Method':
                return <PaymentMethod key={refreshKey} />;
            case 'Terms and Conditions':
                return <TermsAndConditions key={refreshKey} onNavigate={handleNavigation} />;
            case 'Privacy Policy':
                return <PrivacyPolicy key={refreshKey} />;
            case 'Help Center':
                return <HelpCenter key={refreshKey} onNavigate={handleNavigation} previousPage={previousPage} />;
            case 'Dashboard':
            default:
                return (
                    <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: 2 }}>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={6} md={4}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center' }}>
                                    <ManageAccountsIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Welcome to the Admin Panel
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Manage users, settings, and monitor system activity.
                                    </Typography>
                                    <StyledDashboardButton variant="contained" onClick={() => handleNavigation('User Management')}>
                                        Manage Users
                                    </StyledDashboardButton>
                                </InfoCard>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center' }}>
                                    <NotificationsIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Updates & Tips
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Review new features and platform updates.
                                    </Typography>
                                    <StyledDashboardButton variant="contained" onClick={() => handleNavigation('View Updates')}>
                                        View Updates
                                    </StyledDashboardButton>
                                </InfoCard>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center' }}>
                                    <CalendarMonthIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Training Resources
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Access tutorials for platform management.
                                    </Typography>
                                    <StyledDashboardButton variant="contained" onClick={() => handleNavigation('Training')}>
                                        Watch Videos
                                    </StyledDashboardButton>
                                </InfoCard>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center', width: '460px' }}>
                                    <HelpOutlineIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        ShipperDock Community Support
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Ask questions, share experiences, and get solutions from the ShipperDock user community.
                                    </Typography>
                                    <StyledDashboardButton variant="contained" onClick={() => handleNavigation('Community Forum')}>
                                        Community Forum
                                    </StyledDashboardButton>
                                </InfoCard>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center', width: '460px' }}>
                                    <FeedbackIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Feedback
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Review feedback shared by users.
                                    </Typography>
                                    <StyledDashboardButton variant="contained" onClick={() => handleNavigation('View Feedback')}>
                                        View Feedback
                                    </StyledDashboardButton>
                                </InfoCard>
                            </Grid>
                        </Grid>
                    </Container>
                );
        }
    };

    const sidebarContent = (
        <>
            <SidebarHeader>
                        {!isCollapsed && (
                            <Box
                                component="img"
                                src={SD_logo}
                                alt="ShipperDock Admin"
                                sx={{ width: '180px', height: 'auto', borderRadius: '0px'}}
                            />
                        )}
                    </SidebarHeader>
                          <Box sx={{ 
                     flexGrow: 1, 
                     overflowY: 'auto', 
                     overflowX: 'hidden',
                     // Custom Professional Scrollbar
                     '&::-webkit-scrollbar': { width: '5px' },
                     '&::-webkit-scrollbar-track': { background: 'transparent' },
                     // Increased opacity from 0.15 to 0.3 for a "whiter" look
                     '&::-webkit-scrollbar-thumb': { 
                         background: 'rgba(255, 255, 255, 0.45)', 
                         borderRadius: '10px' 
                     },
                     // Increased hover opacity from 0.25 to 0.45
                     '&::-webkit-scrollbar-thumb:hover': { 
                         background: 'rgba(255, 255, 255, 0.45)' 
                     },
                 }}>
                        <List>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Dashboard'} onClick={() => handleNavigation('Dashboard')}>
                            <Tooltip title={isCollapsed ? "Dashboard" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><DashboardIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'User Approval'} onClick={() => handleNavigation('User Approval')}>
                            <Tooltip title={isCollapsed ? "User Approval" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><GppGoodIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="User Approval" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'User Management'} onClick={() => handleNavigation('User Management')}>
                            <Tooltip title={isCollapsed ? "User Management" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><ManageAccountsIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="User Management" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Active Sessions'} onClick={() => handleNavigation('Active Sessions')}>
                            <Tooltip title={isCollapsed ? "Active Sessions" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><MonitorHeartIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Active Sessions" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Trial and Billing Control'} onClick={() => handleNavigation('Trial and Billing Control')}>
                            <Tooltip title={isCollapsed ? "Trial & Billing" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><PaymentIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Trial and Billing Control" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Payment Method'} onClick={() => handleNavigation('Payment Method')}>
                            <Tooltip title={isCollapsed ? "Payment Method" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><PaymentIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Payment Method" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Verification and Status Control'} onClick={() => handleNavigation('Verification and Status Control')}>
                            <Tooltip title={isCollapsed ? "Verification Control" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><GppGoodIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Verification and Status Control" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Review Management'} onClick={() => handleNavigation('Review Management')}>
                            <Tooltip title={isCollapsed ? "Review Management" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><GppGoodIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Review Management" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Equipment Management'} onClick={() => handleNavigation('Equipment Management')}>
                            <Tooltip title={isCollapsed ? "Equipment Management" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><BuildCircleIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Equipment Management" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Load and Truck Monitoring'} onClick={() => handleNavigation('Load and Truck Monitoring')}>
                            <Tooltip title={isCollapsed ? "Monitoring" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><MonitorHeartIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Load and Truck Monitoring" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Post Carriers'} onClick={() => handleNavigation('Post Carriers')}>
    <Tooltip title={isCollapsed ? "Post Carriers" : ""} placement="right">
        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><LocalShippingIcon sx={{ color: 'white' }} /></ListItemIcon>
    </Tooltip>
    {!isCollapsed && <ListItemText primary="Post Carriers" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
</SidebarItem>
<SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Post Brokers'} onClick={() => handleNavigation('Post Brokers')}>
    <Tooltip title={isCollapsed ? "Post Brokers" : ""} placement="right">
        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><BusinessCenterIcon sx={{ color: 'white' }} /></ListItemIcon>
    </Tooltip>
    {!isCollapsed && <ListItemText primary="Post Brokers" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
</SidebarItem>
<SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Post Shippers'} onClick={() => handleNavigation('Post Shippers')}>
    <Tooltip title={isCollapsed ? "Post Shippers" : ""} placement="right">
        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><BusinessIcon sx={{ color: 'white' }} /></ListItemIcon>
    </Tooltip>
    {!isCollapsed && <ListItemText primary="Post Shippers" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
</SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Bulk Post Loads'} onClick={() => handleNavigation('Bulk Post Loads')}>
                            <Tooltip title={isCollapsed ? "Bulk Post Loads" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><TuneIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Bulk Post Loads" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Bulk Post Trucks'} onClick={() => handleNavigation('Bulk Post Trucks')}>
                            <Tooltip title={isCollapsed ? "Bulk Post Trucks" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><LocalShippingIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Bulk Post Trucks" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Directory'} onClick={() => handleNavigation('Directory')}>
                            <Tooltip title={isCollapsed ? "Directory" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><GroupsIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Directory" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                    </List>
                    <Box sx={{ flexGrow: 1 }} />
                    {!isCollapsed && <Divider sx={{ backgroundColor: 'white', my: 1 }} />}
                    <List sx={{ p: 1 }}>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Notifications'} onClick={() => handleNavigation('Notifications')}>
                            <Tooltip title={isCollapsed ? "Notifications" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}>
                                    <Badge color="error" badgeContent={unreadCount}>
                                        <NotificationsIcon sx={{ color: 'white' }} />
                                    </Badge>
                                </ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Notifications" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Updates and Tips'} onClick={() => handleNavigation('Updates and Tips')}>
                            <Tooltip title={isCollapsed ? "Updates & Tips" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><TipsAndUpdatesIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Manage Updates & Tips" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Training Management'} onClick={() => handleNavigation('Training Management')}>
                            <Tooltip title={isCollapsed ? "Training" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><SchoolIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Training Management" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Template Management'} onClick={() => handleNavigation('Template Management')}>
                            <Tooltip title={isCollapsed ? "Templates" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><DescriptionIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Template Management" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                        </SidebarItem>
                        <SidebarItem collapsed={isCollapsed} ref={toolsRef} button onClick={handleToolsClick}>
                            <Tooltip title={isCollapsed ? "Tools" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><BuildIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Tools" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                            {!isCollapsed && (openTools ? <ExpandLess sx={{ fontSize: '1.1rem', opacity: 0.7 }} /> : <ExpandMore sx={{ fontSize: '1.1rem', opacity: 0.7 }} />)}
                        </SidebarItem>
                        <Collapse in={openTools} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <NestedSidebarItem button selected={currentPage === 'Templates'} onClick={() => handleNavigation('Templates')}>
                                    <ListItemIcon><DescriptionIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Templates Viewer" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'Market Map'} onClick={() => handleNavigation('Market Map')}>
                                    <ListItemIcon><MapIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Market Map" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'Company Profile'} onClick={() => handleNavigation('Company Profile')}>
                                    <ListItemIcon><BusinessIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Company Profile" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                            </List>
                        </Collapse>

                        <SidebarItem collapsed={isCollapsed} ref={supportRef} button onClick={handleSupportClick}>
                            <Tooltip title={isCollapsed ? "Support" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><SupportIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="Support" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                            {!isCollapsed && (openSupport ? <ExpandLess sx={{ fontSize: '1.1rem', opacity: 0.7 }} /> : <ExpandMore sx={{ fontSize: '1.1rem', opacity: 0.7 }} />)}
                        </SidebarItem>
                        <Collapse in={openSupport} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <NestedSidebarItem button selected={currentPage === 'Admin Chat'} onClick={() => handleNavigation('Admin Chat')}>
                                    <ListItemIcon><ChatIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Admin Chat" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'Community Forum'} onClick={() => handleNavigation('Community Forum')}>
                                    <ListItemIcon><HelpOutlineIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Community Forum" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'Support Moderation'} onClick={() => handleNavigation('Support Moderation')}>
                                    <ListItemIcon><RateReviewIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Forum Moderation" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'Help Center'} onClick={() => handleNavigation('Help Center')}>
                                    <ListItemIcon><InfoIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Help Center" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'View Feedback'} onClick={() => handleNavigation('View Feedback')}>
                                    <ListItemIcon><SendIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="View Feedback" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                            </List>
                        </Collapse>
                        <SidebarItem collapsed={isCollapsed} ref={accountRef} button onClick={handleAccountClick}>
                            <Tooltip title={isCollapsed ? "Account" : ""} placement="right">
                                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><AccountCircleIcon sx={{ color: 'white' }} /></ListItemIcon>
                            </Tooltip>
                            {!isCollapsed && <ListItemText primary="My Account" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                            {!isCollapsed && (openAccount ? <ExpandLess sx={{ fontSize: '1.1rem', opacity: 0.7 }} /> : <ExpandMore sx={{ fontSize: '1.1rem', opacity: 0.7 }} />)}
                        </SidebarItem>
                        <Collapse in={openAccount} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <NestedSidebarItem button selected={currentPage === 'Account Information'} onClick={() => handleNavigation('Account Information')}>
                                    <ListItemIcon><AccountBoxIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Account Information" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'Privacy Policy'} onClick={() => handleNavigation('Privacy Policy')}>
                                    <ListItemIcon><PolicyIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Privacy Policy" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button selected={currentPage === 'Terms and Conditions'} onClick={() => handleNavigation('Terms and Conditions')}>
                                    <ListItemIcon><DescriptionIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Terms & Conditions" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                                </NestedSidebarItem>
                                <NestedSidebarItem button onClick={onLogout}>
                                    <ListItemIcon><LogoutIcon sx={{ color: 'white' }} /></ListItemIcon>
                                    <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: '0.7rem', fontWeight: 'bold' }} />
                                </NestedSidebarItem>
                            </List>
                        </Collapse>
                    </List>
                    </Box>
                    <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
                    <Box sx={{ display: 'flex', justifyContent: isCollapsed ? 'center' : 'flex-end', p: 1 }}>
                        <Tooltip title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"} placement="right">
                            <IconButton onClick={toggleSidebar} sx={{ color: 'white' }}>
                                {isCollapsed ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ChevronRightIcon sx={{ fontSize: '1.2rem' }} />
                                        <ChevronRightIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                                        <ChevronRightIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ChevronLeftIcon sx={{ fontSize: '1.2rem' }} />
                                        <ChevronLeftIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                                        <ChevronLeftIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                                    </Box>
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
        </>
    );

    return (
        <ThemeProvider theme={theme}>
            <StyledBox>
                {!isMobile && (
                    <Sidebar collapsed={isCollapsed}>
                        {sidebarContent}
                </Sidebar>
                )}

                {/* ─── MOBILE DRAWER (hidden on desktop) ─── */}
                {isMobile && (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                background: 'linear-gradient(to bottom, #0e1d3a, #11224d)',
                                color: 'white',
                                boxSizing: 'border-box',
                                overflowY: 'auto',
                            },
                        }}
                    >
                        {sidebarContent}
                    </Drawer>
                )}

                 {/* ─── CARRIER-STYLE HEADER ─── */}
               <DashboardHeader
    collapsed={isCollapsed}
    sx={{
        left: isMobile ? 0 : (isCollapsed ? collapsedWidth : drawerWidth),
        width: isMobile ? '100%' : `calc(100% - ${isCollapsed ? collapsedWidth : drawerWidth}px)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between', // Pushes left box and right items apart
        paddingX: 0,
    }}
>
    {/* LEFT SECTION: Placeholder or empty space if no nav items here */}
    <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%', ml: 0 }}>
        {isMobile && (
            <Box
                onClick={handleDrawerToggle}
                sx={{
                    display: 'flex', alignItems: 'center', px: '12px',
                    cursor: 'pointer',
                    borderRight: '2px solid rgba(0,0,0,0.1)',
                    color: 'rgba(0,0,0,0.7)',
                    transition: 'all 0.2s',
                    '&:hover': { background: '#0e1d3a', color: '#fff' },
                }}
            >
                <Badge color="error" badgeContent={unreadCount} invisible={currentPage === 'Notifications'}>
                    <MenuIcon sx={{ fontSize: '1.1rem' }} />
                </Badge>
            </Box>
        )}
    </Box>

    {/* RIGHT SECTION: Page Name & Red Logout */}
    <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
        <PageNameTag>
            <Box
                className="page-dot"
                sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: '#0e1d3a',
                    flexShrink: 0,
                    transition: 'background 0.2s ease',
                }}
            />
            <Typography
                className="page-label"
                sx={{
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    color: 'rgba(0,0,0,0.8)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.9px',
                    transition: 'color 0.2s ease',
                }}
            >
                {currentPage}
            </Typography>
        </PageNameTag>

        {/* RED LOGOUT ICON */}
        <NavTab
            active={0}
            onClick={onLogout}
            sx={{
                color: '#ffffff', // Red color
                borderLeft: '2px solid rgba(0,0,0,0.13)', // Divider from PageNameTag
                borderRight: 'none',
                '& .MuiSvgIcon-root': { color: '#ffffff' },
                '&:hover': {
                    background: '#dc2626', // Red background on hover
                    color: '#ffffff',
                    '& .MuiSvgIcon-root': { color: '#ffffff' },
                },
            }}
        >
            <LogoutIcon sx={{ fontSize: '0.9rem' }} />
            {!isMobile && 'Logout'}
        </NavTab>
    </Box>
</DashboardHeader>
                <MainContent
                    collapsed={isCollapsed}
                    hasRightSidebar={false}
                    sx={{
                        paddingTop:
                            currentPage === "Dashboard"
                                ? `calc(${dashboardHeaderHeight} + 24px)`
                                : `calc(${dashboardHeaderHeight} + 1px)`,
                        paddingX: 0,
                        paddingBottom: 0,
                        backgroundColor: '#f4f6f8',
                        minHeight: '100vh',
                        overflowY: currentPage === "Dashboard" ? "hidden" : "auto",
                        overflowX: "hidden",
                    }}
                >
                    {renderPage()}
                </MainContent>
                <ChatFab onClick={() => handleNavigation('Admin Chat')}>
                    <ChatIcon />
                </ChatFab>
            </StyledBox>
        </ThemeProvider>
    );
};

export default AdminDashboard;